import { Model as ORMModel } from '@vuex-orm/core'

/**
 * Wrapper for models to provide model level convenience methods for
 * interacting with the store and persistence.
 * Will be built into vuex-orm in the future. See https://github.com/vuex-orm/vuex-orm/issues/60
 *
 * Requires the inflected npm package.
 */
export default class Model extends ORMModel {
  /**
   * Helper method which creates a Vuex-ORM query object containing all relations.
   */
  static buildQuery () {
    return this.getters('query')();
  }

  /**
   * Get all records from vuex store.
   */
  static findAll () {
    const query = this.buildQuery()
    return query.all()
  }

  static where (filter) {
    let query = this.buildQuery()
    query = query.where(filter)
    return query
  }

  static whereIdIn (filter) {
    let query = this.buildQuery()
    query = query.whereIdIn(filter)
    return query
  }

  /**
   * Find single record from vuex store.
   *
   * @param {string} id - ID to search for.
   */
  static find (id) {
    const query = this.buildQuery()
    return query.where('id', id).first()
  }

  /**
   * Get the first record form the vuex store.
   */
  static first () {
    const query = this.buildQuery()
    return query.first()
  }

  /**
   * Get the last record form the vuex store.
   */
  static last () {
    const query = this.buildQuery()
    return query.last()
  }

  /**
   * Create object in vuex store and returns it.
   *
   * @param {Object} data - Data to create object from.
   */
  static async create (data) {
    const insertedData = await this.insert({ data })
    const ary = insertedData[this.entity]
    return this.find(ary[ary.length - 1].id)
  }

  /**
   * Update vuex store and returns the stores record. Unlike the original this also saves relations.
   *
   * @returns {Promise<Object>}
   */
  async $save () {
    const insertedData = await this.$update(this)
    const ary = insertedData[this.constructor.entity]
    return ary[ary.length - 1]
  }

  /**
   * Deletes all records from server.
   *
   * @returns {Promise}
   */
  static async destroyAll () {
    return this.dispatch('destroyAll')
  }

  /**
   * Deletes all from vuex store.
   *
   * @returns {Promise}
   */
  static async deleteAll () {
    return this.dispatch('deleteAll')
  }

  /**
   * Delete from vuex store via filter closure.
   *
   * @param {Object|Function} filter - Delete data by closure or object.
   */
  static async delete (filter) {
    return this.dispatch('delete', filter)
  }

  /**
   * Reverts the records property values to the stores state.
   *
   * @returns {Promise}
   */
  async $revert () {
    const recordFromStore = this.constructor.find(this.id).$toJson()

    Object.keys(recordFromStore).forEach((key) => {
      const value = recordFromStore[key]
      if (!key.startsWith('$') && typeof value !== 'object') {
        this[key] = value
      }
    })
  }

  /**
   * Tells if there are any changed properties (compared to the state in the store).
   *
   * @returns {boolean}
   */
  get $isDirty () {
    const recordFromStore = this.constructor.find(this.id).$toJson()

    const dirtyProperty = Object.keys(recordFromStore).find((key) => {
      const value = recordFromStore[key]
      return !key.startsWith('$') && typeof value !== 'object' && value !== this[key]
    })

    return !!dirtyProperty
  }
}
